/**
 * /contribute — Community fine-tuning program for RAELA.
 *
 * Members can opt in to share anonymized interaction signals that
 * directly improve RAELA's matching, safety triage, and conversation
 * quality. Opting in is reversible at any time. First-time opt-in
 * grants a one-month free upgrade (delivered by email within 24h).
 *
 * No PII, message bodies, photos, or location data ever leaves the
 * user's account in identifiable form. This page documents exactly
 * what is shared and how to revoke participation.
 */
import { Link, useNavigate } from "react-router-dom";
import { useMemo } from "react";
import { ArrowLeft, Brain, CheckCircle2, Gift, Lock, ShieldCheck, Sparkles, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useContributionOptIn } from "@/lib/contribute/useContributionOptIn";
import { toast } from "@/hooks/use-toast";

const SHARED = [
  "Anonymized prompt / reply pairs from your RAELA chats (no names, handles, or contact info)",
  "Match-quality signals (you swiped/skipped, messaged, met) — IDs only, never names",
  "Safety triage outcomes (was a flag accurate?) so RAELA learns to protect better",
  "Aggregate session metadata (length, tier, language) — never linked back to you",
];

const NEVER = [
  "Direct messages between you and other members",
  "Photos, voice notes, or video",
  "Location, device IDs, or precise GPS",
  "Payment details, KYC documents, or government IDs",
  "Anything that could re-identify you to a human reviewer",
];

const STEPS = [
  {
    title: "You opt in",
    body: "Toggle below. Nothing changes in your experience — RAELA stays the same.",
  },
  {
    title: "We anonymize",
    body: "An automated pipeline strips identifiers before any data leaves your account. No human reads raw chats.",
  },
  {
    title: "Models train on the signal",
    body: "Your contributions join a vetted training set used to improve matching, safety, and conversational tone.",
  },
  {
    title: "RAELA gets sharper for everyone",
    body: "Better suggestions, fewer false safety flags, more on-tone replies — for you and the whole community.",
  },
  {
    title: "You stay in control",
    body: "Opt out at any time. Future contributions stop immediately. Past contributions can be purged via DSAR.",
  },
];

const Contribute = () => {
  const navigate = useNavigate();
  const { optedIn, optedInAt, isAuthenticated, pending, optIn, optOut } =
    useContributionOptIn();

  const optedInLabel = useMemo(
    () => (optedInAt ? new Date(optedInAt).toLocaleDateString() : null),
    [optedInAt],
  );

  const handleOptIn = async () => {
    if (!isAuthenticated) {
      toast({
        title: "Sign in required",
        description: "Create an account or sign in to join the contributor program.",
      });
      return;
    }
    try {
      const { rewardJustGranted } = await optIn();
      toast({
        title: rewardJustGranted
          ? "You're in — and your free month is on us 🎁"
          : "Welcome back, contributor",
        description: rewardJustGranted
          ? "Your one-month free upgrade code will arrive by email within 24 hours."
          : "Your contributions are active again. Thank you.",
      });
    } catch (e) {
      toast({
        title: "Something went wrong",
        description: e instanceof Error ? e.message : "Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleOptOut = async () => {
    await optOut();
    toast({
      title: "Opted out",
      description: "Future contributions are paused. Existing reward (if any) is yours to keep.",
    });
  };

  return (
    <main className="min-h-screen bg-background pb-24 md:pb-12">
      {/* Hero */}
      <section className="border-b border-border bg-gradient-to-b from-primary/5 to-transparent">
        <div className="mx-auto max-w-4xl px-6 py-12 md:py-20">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/"))}
            className="mb-6 -ml-2 gap-1.5 text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
          <Badge variant="secondary" className="mb-4 gap-1.5">
            <Sparkles className="h-3 w-3" />
            Community fine-tuning
          </Badge>
          <h1 className="font-display text-4xl md:text-5xl font-semibold tracking-tight text-foreground">
            Help train RAELA. Get a free month on us.
          </h1>
          <p className="mt-4 max-w-2xl text-lg text-muted-foreground">
            RAELA learns from real conversations, real matches, and real safety
            calls. Members who opt in shape every future version — anonymously,
            reversibly, and on your terms.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            {optedIn ? (
              <>
                <Badge className="gap-1.5 px-3 py-1.5 text-sm" variant="default">
                  <CheckCircle2 className="h-4 w-4" />
                  Contributing{optedInLabel ? ` since ${optedInLabel}` : ""}
                </Badge>
                <Button variant="outline" onClick={handleOptOut} disabled={pending}>
                  <XCircle className="mr-2 h-4 w-4" />
                  {pending ? "Saving…" : "Opt out"}
                </Button>
              </>
            ) : (
              <Button size="lg" onClick={handleOptIn} className="gap-2" disabled={pending}>
                <Gift className="h-4 w-4" />
                {pending ? "Saving…" : "Opt in & claim my free month"}
              </Button>
            )}
            <Link
              to="/ai-policy"
              className="text-sm text-muted-foreground underline-offset-4 hover:underline"
            >
              Read the full AI policy →
            </Link>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-4xl space-y-10 px-6 py-12">
        {/* Why */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Brain className="h-5 w-5 text-primary" />
              Why your contribution matters
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-muted-foreground">
            <p>
              Off-the-shelf models don't understand neurodivergent communication
              styles, queer dating nuance, mental-health context, or the
              specific safety patterns our community sees. RAELA does — but only
              because real members teach her.
            </p>
            <p>
              Every opt-in makes matching more accurate, safety triage less
              noisy, and replies more on-tone. It's the single biggest lever we
              have for product quality, and it costs you nothing.
            </p>
          </CardContent>
        </Card>

        {/* The process */}
        <section>
          <h2 className="font-display text-2xl font-semibold text-foreground">
            How it works
          </h2>
          <ol className="mt-6 space-y-4">
            {STEPS.map((step, i) => (
              <li
                key={step.title}
                className="flex gap-4 rounded-lg border border-border bg-card p-5"
              >
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 font-medium text-primary">
                  {i + 1}
                </div>
                <div>
                  <h3 className="font-medium text-foreground">{step.title}</h3>
                  <p className="mt-1 text-sm text-muted-foreground">{step.body}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>

        {/* What's shared / what's never shared */}
        <section className="grid gap-6 md:grid-cols-2">
          <Card className="border-primary/20">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <ShieldCheck className="h-5 w-5 text-primary" />
                What's shared (anonymized)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm text-muted-foreground">
                {SHARED.map((line) => (
                  <li key={line} className="flex gap-2">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          <Card className="border-destructive/20">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Lock className="h-5 w-5 text-destructive" />
                What never leaves your account
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm text-muted-foreground">
                {NEVER.map((line) => (
                  <li key={line} className="flex gap-2">
                    <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </section>

        {/* Reward */}
        <Card className="bg-gradient-to-br from-primary/10 to-accent/10">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Gift className="h-5 w-5 text-primary" />
              Your reward: one month free
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-muted-foreground">
            <p>
              The first time you opt in, we send you a single-use promo code
              good for one free month on any paid tier (Tuned, Attuned, or
              Resonant). The code arrives by email within 24 hours and never
              expires.
            </p>
            <p>
              Already a paid member? Apply it to your next renewal. Free member?
              Use it to try a tier you've been curious about. Opting back out
              later doesn't claw the reward back — it's yours.
            </p>
          </CardContent>
        </Card>

        {/* Opt-out reassurance */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">You can change your mind anytime</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-muted-foreground">
            <p>
              Toggle off at the top of this page and future contributions stop
              immediately. To purge contributions already in our training set,
              file a Data Subject Access Request (DSAR) from{" "}
              <Link to="/account" className="underline">
                Account → Privacy
              </Link>
              .
            </p>
            <p>
              The contributor program is governed by our{" "}
              <Link to="/ai-policy" className="underline">
                AI Policy
              </Link>{" "}
              and{" "}
              <Link to="/privacy" className="underline">
                Privacy Policy
              </Link>
              .
            </p>
          </CardContent>
        </Card>

        {/* Sticky CTA bottom */}
        {!optedIn && (
          <div className="sticky bottom-20 md:bottom-6 mx-auto flex max-w-md justify-center">
            <Button size="lg" className="w-full gap-2 shadow-lg" onClick={handleOptIn} disabled={pending}>
              <Gift className="h-4 w-4" />
              {pending ? "Saving…" : "Opt in & claim my free month"}
            </Button>
          </div>
        )}
      </div>
    </main>
  );
};

export default Contribute;
